'use client';

import { useCookieConsent } from '@/components/layout/CookieConsent/CookieConsentProvider';
import { ManageCookiePreferencesButton } from '@/components/layout/CookieConsent/ManageCookiePreferencesButton';
import {
  COOKIES_CONSENT_VALUE,
  type CookiesConsentValue,
} from '@/constants/cookies';
import { formatConsentDate, formatDeclarationDate } from '@/lib/format';

import styles from './CookieConsentDeclarationStatus.module.css';

import type { Messages } from '@/content/messages';
import type { Locale } from '@/lib/i18n';

type CookieConsentDeclarationStatusProps = {
  locale: Locale;
  messages: Messages['cookies']['settings'];
  /** Hostname from `getSiteOrigin()` (server-resolved). */
  domain: string;
};

function stateLabel(
  consent: CookiesConsentValue | null,
  messages: Messages['cookies']['settings'],
): string {
  if (consent === COOKIES_CONSENT_VALUE.granted) return messages.stateAllow;
  if (consent === COOKIES_CONSENT_VALUE.denied) return messages.stateDeny;
  return messages.stateUnset;
}

/**
 * Client island: live consent state / ID / date from localStorage, plus
 * “Change your consent” and today’s declaration-updated line.
 */
export function CookieConsentDeclarationStatus({
  locale,
  messages,
  domain,
}: CookieConsentDeclarationStatusProps) {
  const { ready, consent, record } = useCookieConsent();

  const state = ready ? stateLabel(consent, messages) : messages.stateUnset;
  const consentId = ready ? record?.id : null;
  const decidedAt = ready ? record?.decidedAt : null;
  const updatedLabel = messages.declarationUpdated.replace(
    '{date}',
    formatDeclarationDate(locale),
  );
  const domainsLabel = messages.domains.replace('{domain}', domain);

  return (
    <div className={styles.root}>
      <p className={styles.domains}>{domainsLabel}</p>

      <dl className={styles.status}>
        <div className={styles.row}>
          <dt>{messages.currentStateLabel}</dt>
          <dd>{state}</dd>
        </div>
        {consentId ? (
          <div className={styles.row}>
            <dt>{messages.consentIdLabel}</dt>
            <dd className={styles.mono}>{consentId}</dd>
          </div>
        ) : null}
        {decidedAt ? (
          <div className={styles.row}>
            <dt>{messages.consentDateLabel}</dt>
            <dd>{formatConsentDate(locale, decidedAt)}</dd>
          </div>
        ) : null}
      </dl>

      <div className={styles.actions}>
        <ManageCookiePreferencesButton label={messages.changeConsent} />
      </div>

      <p className={styles.updated}>{updatedLabel}</p>
    </div>
  );
}
